import { Link } from "react-router";
import { docsPath } from "./docsNav";

export function DocsSessionsPage() {
  return (
    <div className="docs-article">
      <p className="docs-eyebrow">Realtime</p>
      <h1 className="docs-page-title">Sessions</h1>
      <p className="docs-lede">
        Each Running product maps to one managed Woven session. Woven Host provisions the namespace
        and session, applies its CCU allocation, and returns the exact assigned scope in a private
        connection descriptor.
      </p>

      <div className="panel notice-panel docs-spatial-notice">
        <p className="page-prose">
          <strong>This is the active managed path.</strong> Reveal the Running product&apos;s
          connection details in the console and treat the returned token and TLS material as
          secrets. The descriptor is the source of truth for connecting.
        </p>
      </div>

      <h2 className="docs-heading">The descriptor defines the session boundary</h2>
      <dl className="detail-list">
        <div className="detail-row">
          <dt>Endpoint and TLS</dt>
          <dd>
            Native clients use <code>endpoint</code> and <code>caPem</code> or an
            application-provided trusted root store. The Online browser path uses{" "}
            <code>webTransport.url</code> and the optional lowercase-hex{" "}
            <code>webTransport.certificateSha256</code> pin. Browser JavaScript cannot install{" "}
            <code>caPem</code> as an arbitrary trust root.
          </dd>
        </div>
        <div className="detail-row">
          <dt>Managed scope</dt>
          <dd>
            <code>namespaceId</code> and <code>sessionId</code> identify the one session authorized
            by this descriptor. The token is limited to that managed product.
          </dd>
        </div>
        <div className="detail-row">
          <dt>Spaces</dt>
          <dd>
            The Lite connection descriptor returns spaces <code>1</code> and <code>2</code>, both at
            epoch <code>1</code>, with a <code>Logical</code> coordinate frame and{" "}
            <code>BroadcastAll</code> routing.
          </dd>
        </div>
        <div className="detail-row">
          <dt>Channels</dt>
          <dd>
            Both spaces expose exactly channels <code>1</code> and <code>4</code>: reliable ordered
            delivery on channel <code>1</code> (<code>ReliableOrdered</code>) and unreliable
            sequenced delivery on channel <code>4</code> (<code>UnreliableSequenced</code>). Both
            are
            <code> Ephemeral</code> with a 64 KiB payload ceiling and generic opaque payloads.
            Channels <code>2</code> and <code>3</code> remain disallowed. Channel policy is
            server-controlled and cannot be overridden by a client. Host refuses to reveal a
            descriptor if the live node does not match this exact contract.
          </dd>
        </div>
      </dl>

      <h2 className="docs-heading">Admission joins the managed session</h2>
      <p className="docs-body">
        Connect with the descriptor token using explicit Bearer authentication. Before subscribing
        or publishing, call <code>request_admission</code> or <code>admit_with_cancellation</code>{" "}
        in Rust, or <code>requestAdmission</code> or <code>admitWithCancellation</code> in the
        TypeScript browser client. An admitted result atomically consumes one CCU slot and joins the
        session. The ordinary session join path is rejected for managed sessions.
      </p>
      <dl className="detail-list">
        <div className="detail-row">
          <dt>Admitted</dt>
          <dd>The connection is already a session member and may proceed to subscribe.</dd>
        </div>
        <div className="detail-row">
          <dt>Queued</dt>
          <dd>
            The connection owns a queue ticket. Use <code>queue_heartbeat</code> while waiting and
            <code>queue_claim</code> after an offer; <code>queue_status</code> and
            <code>queue_cancel</code> are also available.
          </dd>
        </div>
        <div className="detail-row">
          <dt>Paused or rejected</dt>
          <dd>
            Treat the result as a semantic outcome. Do not invent a session, bypass admission, or
            silently retry transport I/O.
          </dd>
        </div>
      </dl>
      <p className="docs-body">
        Disconnecting releases the managed admission and makes capacity available to the queue. See
        the{" "}
        <Link to={docsPath("sdks/rust")} className="docs-inline-link">
          Rust client
        </Link>{" "}
        or{" "}
        <Link to={docsPath("sdks/javascript")} className="docs-inline-link">
          TypeScript client
        </Link>{" "}
        requirements before opening a managed connection.
      </p>

      <h2 className="docs-heading">Availability scope</h2>
      <p className="docs-body">
        The Lite tier uses the logical and managed 3D spatial definitions plus Ephemeral channels
        returned in its descriptor. Native QUIC, browser WebTransport, the TypeScript browser
        client, and managed spatial routing are Online. Persistence beyond Ephemeral remains
        Preview. Pro and Dedicated Preview access begins privately with founders; every client
        continues to follow the exact supported scope returned by Woven Host.
      </p>
    </div>
  );
}
